import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { restoreDetailText } from "../pages/DaySchedule/CustomPrintout/customPrintoutText";
import type { CustomPrintoutDetail, CustomPrintoutSummary } from "../pages/DaySchedule/CustomPrintout/customPrintoutTypes";
import { backendService } from "../services/backend.service";

export const customPrintoutKeys = {
	all: ["customPrintouts"] as const,
	list: (departmentId: string) => [...customPrintoutKeys.all, departmentId, "list"] as const,
	detail: (departmentId: string, printoutId: string) => [...customPrintoutKeys.all, departmentId, "detail", printoutId] as const,
	versions: (departmentId: string, printoutId: string) => [...customPrintoutKeys.all, departmentId, "versions", printoutId] as const,
	version: (departmentId: string, printoutId: string, versionId: string) =>
		[...customPrintoutKeys.all, departmentId, "version", printoutId, versionId] as const,
};

/** The department's custom printouts, newest edits included (no template source). */
export const useCustomPrintoutList = () => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	return useQuery({
		queryKey: customPrintoutKeys.list(departmentId),
		queryFn: async () => (await backendService.listCustomPrintouts(departmentId)).data,
	});
};

/** The detail query's key while nothing is chosen: the query is disabled then, so it only has to be stable. */
const NO_PRINTOUT_KEY = "none";

/** One printout's current version with its source; idle while nothing is chosen. */
export const useCustomPrintoutDetail = (printoutId: string | null) => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	return useQuery({
		queryKey: customPrintoutKeys.detail(departmentId, printoutId === null ? NO_PRINTOUT_KEY : printoutId),
		queryFn: async () => {
			if (printoutId === null) throw new Error("useCustomPrintoutDetail ran with no printout chosen");
			return restoreDetailText((await backendService.getCustomPrintout(departmentId, printoutId)).data);
		},
		enabled: printoutId !== null,
	});
};

type MutationEvent = "custom_printout_created" | "custom_printout_saved" | "custom_printout_renamed" | "custom_printout_deleted";

/**
 * Shared plumbing: the answer goes into the cache (`updateCache`, before anything that reads it re-renders), the
 * list reloads, success is tracked, and a failure is shown — `showErrorSnackbar` tracks it too.
 */
const useCustomPrintoutMutation = <TVariables, TResult extends CustomPrintoutSummary | CustomPrintoutDetail | { id: string }>(options: {
	event: MutationEvent;
	failureKey: string;
	run: (departmentId: string, variables: TVariables) => Promise<TResult>;
	printoutIdOf: (variables: TVariables, result: TResult | null) => string | null;
	updateCache: (queryClient: QueryClient, departmentId: string, result: TResult, variables: TVariables) => void;
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();
	const queryClient = useQueryClient();
	const departmentId = chosenDepartment.department_id;
	return useMutation<TResult, unknown, TVariables>({
		mutationFn: (variables) => options.run(departmentId, variables),
		onSuccess: (result, variables) => {
			options.updateCache(queryClient, departmentId, result, variables);
			void queryClient.invalidateQueries({ queryKey: customPrintoutKeys.list(departmentId) });
			track(options.event, { department_id: departmentId, printout_id: options.printoutIdOf(variables, result) });
		},
		onError: (error, variables) => {
			showErrorSnackbar(
				t(options.failureKey),
				{ event: `${options.event}_error`, department_id: departmentId, printout_id: options.printoutIdOf(variables, null) },
				error,
			);
		},
	});
};

/** A printout's new current version: shown without a fetch, and its version history reloads to include it. */
export const storeDetail = (queryClient: QueryClient, departmentId: string, detail: CustomPrintoutDetail): void => {
	queryClient.setQueryData(customPrintoutKeys.detail(departmentId, detail.id), detail);
	void queryClient.invalidateQueries({ queryKey: customPrintoutKeys.versions(departmentId, detail.id) });
};

/**
 * A printout's list row after its current version changed: its version number, summary and edit time show at once,
 * not when the list has reloaded. A row that isn't listed yet is left to that reload.
 */
export const storeListRow = (queryClient: QueryClient, departmentId: string, detail: CustomPrintoutDetail): void => {
	queryClient.setQueryData<CustomPrintoutSummary[]>(customPrintoutKeys.list(departmentId), (rows) =>
		rows === undefined
			? rows
			: rows.map((row) => (row.id === detail.id ? { ...row, versionNumber: detail.versionNumber, summary: detail.summary, updatedAt: detail.updatedAt } : row)),
	);
};

export const useCreateCustomPrintout = () =>
	useCustomPrintoutMutation<{ name: string; generationId: string }, CustomPrintoutDetail>({
		event: "custom_printout_created",
		failureKey: "printout.custom.ai.save.failed",
		run: async (departmentId, { name, generationId }) =>
			restoreDetailText((await backendService.createCustomPrintout({ departmentId, name, generationId })).data),
		printoutIdOf: (_variables, result) => (result === null ? null : result.id),
		updateCache: (queryClient, departmentId, detail) => storeDetail(queryClient, departmentId, detail),
	});

export const useSaveCustomPrintoutVersion = () =>
	useCustomPrintoutMutation<{ printoutId: string; generationId: string }, CustomPrintoutDetail>({
		event: "custom_printout_saved",
		failureKey: "printout.custom.ai.save.failed",
		run: async (departmentId, { printoutId, generationId }) =>
			restoreDetailText((await backendService.saveCustomPrintoutVersion(printoutId, { departmentId, generationId })).data),
		printoutIdOf: ({ printoutId }) => printoutId,
		updateCache: (queryClient, departmentId, detail) => {
			storeDetail(queryClient, departmentId, detail);
			storeListRow(queryClient, departmentId, detail);
		},
	});

export const useRenameCustomPrintout = () =>
	useCustomPrintoutMutation<{ printoutId: string; name: string }, CustomPrintoutSummary>({
		event: "custom_printout_renamed",
		failureKey: "printout.custom.ai.rename.failed",
		run: async (departmentId, { printoutId, name }) => (await backendService.renameCustomPrintout(printoutId, { departmentId, name })).data,
		printoutIdOf: ({ printoutId }) => printoutId,
		updateCache: (queryClient, departmentId, renamed) =>
			queryClient.setQueryData<CustomPrintoutSummary[]>(customPrintoutKeys.list(departmentId), (rows) =>
				rows === undefined ? rows : rows.map((row) => (row.id === renamed.id ? renamed : row)),
			),
	});

export const useDeleteCustomPrintout = () =>
	useCustomPrintoutMutation<{ printoutId: string }, { id: string }>({
		event: "custom_printout_deleted",
		failureKey: "printout.custom.ai.delete.failed",
		run: async (departmentId, { printoutId }) => (await backendService.deleteCustomPrintout(departmentId, printoutId)).data,
		printoutIdOf: ({ printoutId }) => printoutId,
		// Gone from the cache at once, so nothing picks it again and its detail is never fetched (a 404).
		updateCache: (queryClient, departmentId, deleted) => {
			queryClient.setQueryData<CustomPrintoutSummary[]>(customPrintoutKeys.list(departmentId), (rows) =>
				rows === undefined ? rows : rows.filter((row) => row.id !== deleted.id),
			);
			queryClient.removeQueries({ queryKey: customPrintoutKeys.detail(departmentId, deleted.id) });
		},
	});
